import type { Criterion, DecisionOption, RankedOption } from "./types";

export function normalizeCriteriaWeights(criteria: Criterion[]): Criterion[] {
  if (criteria.length === 0) return [];
  const safeWeights = criteria.map((item) => Math.max(0, item.weight));
  const total = safeWeights.reduce((sum, weight) => sum + weight, 0);
  if (total === 0) {
    return normalizeCriteriaWeights(criteria.map((item) => ({ ...item, weight: 1 })));
  }

  const exact = safeWeights.map((weight) => (weight / total) * 100);
  const floored = exact.map(Math.floor);
  let remainder = 100 - floored.reduce((sum, weight) => sum + weight, 0);
  const priority = exact
    .map((weight, index) => ({ index, fraction: weight - floored[index] }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

  for (let index = 0; index < remainder; index += 1) {
    floored[priority[index].index] += 1;
  }
  remainder = 0;

  return criteria.map((item, index) => ({ ...item, weight: floored[index] }));
}

export function calculateOptionScores(
  criteria: Criterion[],
  options: DecisionOption[],
): RankedOption[] {
  const normalized = normalizeCriteriaWeights(criteria);
  return options
    .map((option) => {
      const score = normalized.reduce((sum, criterion) => {
        const rawScore = Math.min(10, Math.max(0, option.scores[criterion.id] ?? 0));
        return sum + rawScore * criterion.weight * 0.1;
      }, 0);
      return { id: option.id, label: option.label, score: Math.round(score) };
    })
    .sort((a, b) => b.score - a.score);
}

export function recommendationConfidence(scores: Array<{ score: number }>): "low" | "medium" | "high" {
  if (scores.length < 2) return "low";
  const sorted = [...scores].sort((a, b) => b.score - a.score);
  const gap = sorted[0].score - sorted[1].score;
  if (gap < 6) return "low";
  if (gap < 15) return "medium";
  return "high";
}
